import MapsApiBaseService from './MapsApiBaseService';
import { ALBERTA } from '../constants';
import AddressComponentParseService from './AddressComponentParseService';
import { CountryCode } from '@/enums/country-code';

const PlaceServiceRequestField = {
  AddressComponents: 'address_component',
  PlaceId: 'place_id',
  AdrAddress: 'adr_address',
  FormattedAddress: 'formatted_address',
  Geometry: 'geometry',
};

const PlaceType = {
  Address: 'address',
};

const PROVINCE_ADDRESS_TYPE = 'administrative_area_level_1';

class GeolocationService extends MapsApiBaseService {
  #autocompleteService = null;
  #geocoder = null;
  #placesService = null;

  async init() {
    await super.init();
    this.#placesService = new this.googleMaps.places.PlacesService(document.createElement('div'));
    this.#autocompleteService = new this.googleMaps.places.AutocompleteService();

    this.#geocoder = new this.googleMaps.Geocoder();
  }

  // eslint-disable-next-line class-methods-use-this
  async getDeviceGeolocation() {
    const { coords } = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject);
    });

    return {
      lat: coords.latitude,
      lng: coords.longitude,
    };
  }

  async getPlaceDetailsByPlaceId(placeId) {
    const fields = Object.values(PlaceServiceRequestField);
    const config = { placeId, fields };

    return new Promise((resolve, reject) => {
      try {
        this.#placesService?.getDetails(config, (place) => {
          const { lng, lat } = place.geometry.location;

          resolve({
            longitude: lng?.(),
            latitude: lat?.(),
            ...AddressComponentParseService.parseComponentsToAddressObject(
              place.address_components,
            ),
          });
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  async searchAddressesByText(text) {
    return this.searchPlacesByText(text, {
      types: [PlaceType.Address],
      componentRestrictions: { country: CountryCode.Canada },
    });
  }

  async searchPlacesByText(text, options = {}) {
    if (!text) {
      return null;
    }

    const { predictions } = await this.#autocompleteService.getPlacePredictions({
      bounds: this.#createBounds(),
      input: text,
      ...options,
    });

    return predictions;
  }

  async reverseGeoCode(lat, lng) {
    const latLng = {
      lat: parseFloat(lat),
      lng: parseFloat(lng),
    };

    const province = await this.#geocoder
      .geocode({ location: latLng })
      .then((response) => {
        if (response.results[0]) {
          const value = response.results[0].address_components;
          const [filteredValue] = value
            .filter((item) => item.types.includes(PROVINCE_ADDRESS_TYPE));

          return filteredValue.short_name;
        }

        return null;
      });

    return province;
  }

  async getPlaceIdLocation(placeId) {
    return GeolocationService.#getLocationFromGeocode(await this.geocode({ placeId }));
  }

  async getAddressLocation(address) {
    return GeolocationService.#getLocationFromGeocode(await this.geocode({ address }));
  }

  geocode(searchedPlace) {
    return this.#geocoder.geocode(searchedPlace);
  }

  #createBounds(bounds = ALBERTA) {
    return new this.googleMaps.LatLngBounds(bounds);
  }

  static #getLocationFromGeocode(geocodeResult) {
    const [place] = geocodeResult.results;

    return place.geometry.location;
  }
}

export default new GeolocationService();
